import { FC } from 'react'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import AppBar from '@mui/material/AppBar'
import Paper from '@mui/material/Paper'

import { getPageData } from '@/router/RoutesData'
import { useSizes } from '@/hooks/useSizes'
import logo from '@/assets/logoTwoColors.svg'
import { useDefaultAuthRedirect } from '@/pages/SignIn/utils'
import { useAssistant } from '@/components/Assistant/AssistantProvider'
import AssistantTopBar from '@/components/Assistant/AssistantTopBar'
import { useAssistantBottomInset } from '@/components/Assistant/layout'
import { NavigationTestIds } from '../../../automation/playwright/test-ids'
import {
  APP_BAR_GAP_WITH_ASSISTANT,
  APP_BAR_TOOLBAR_HEIGHT,
  APP_BAR_USER_MENU_GAP,
  APP_BAR_Z_INDEX,
  MOBILE_BOTTOM_NAV_HEIGHT,
} from './constants'
import Links from './Links'
import UserMenu from './UserMenu'

interface IApplicationBarProps {
  skipBottomBar?: boolean
}

const ApplicationBar: FC<IApplicationBarProps> = ({
  skipBottomBar,
}: IApplicationBarProps) => {
  const location = useLocation()
  const pageData = getPageData(`/${location.pathname.split('/')[1]}`) // to account for parametrized routes
  const isMobileBackBar =
    (pageData && 'mobileBackNavbar' in pageData && pageData.mobileBackNavbar) ??
    false
  const { screenSize } = useSizes()
  const mobileLayout = screenSize !== 'desktop'
  const navigate = useNavigate()
  const showMobileBackBar = isMobileBackBar && mobileLayout

  const navPath = useDefaultAuthRedirect()
  const showBottomBar = mobileLayout && !skipBottomBar
  // On tablets the docked Ask Attorney Share panel stops above the bottom nav.
  useAssistantBottomInset(showBottomBar ? MOBILE_BOTTOM_NAV_HEIGHT : 0)
  // Ask Attorney Share's circle needs room; without it (flag off, signed
  // out) the bar keeps its spacing.
  const showAssistant = Boolean(useAssistant()?.isAvailable)
  let toolbarGap: number | typeof APP_BAR_GAP_WITH_ASSISTANT = 4
  if (showAssistant) {
    toolbarGap = showMobileBackBar ? 2 : APP_BAR_GAP_WITH_ASSISTANT
  }

  return (
    <AppBar
      position="sticky"
      sx={theme => ({
        width: '100%',
        px: { xs: 2, md: 3 },
        zIndex: APP_BAR_Z_INDEX,
        background: theme.palette.common.white,
        boxShadow: '0px 3px 1px -2px #00000033',
      })}
      id="static-navigation-bar"
      elevation={0}
    >
      <>
        <Toolbar
          disableGutters
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            height: APP_BAR_TOOLBAR_HEIGHT,
            gap: toolbarGap,
          }}
        >
          {showMobileBackBar ? (
            <>
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBackOutlinedIcon />
              </IconButton>

              <Typography
                variant="subtitle1"
                color="text.primary"
                sx={{
                  flexGrow: 1,
                }}
              >
                {pageData?.title}
              </Typography>
              <AssistantTopBar compact />
            </>
          ) : (
            <>
              <Link to={navPath} data-testid={NavigationTestIds.logo}>
                <img alt="logo" src={logo} width={87} />
              </Link>

              {!mobileLayout && (
                <Box data-testid={NavigationTestIds.desktopNavContainer}>
                  <Links dense={showAssistant} />
                </Box>
              )}
              <Box
                display="flex"
                alignItems="center"
                gap={APP_BAR_USER_MENU_GAP}
              >
                <AssistantTopBar compact={mobileLayout} />
                <UserMenu dense={showAssistant} />
              </Box>
            </>
          )}
          <Box
            data-testid={NavigationTestIds.navigationContainer}
            sx={{ display: 'none' }}
          />
        </Toolbar>

        {showBottomBar && (
          <Paper
            data-testid={NavigationTestIds.mobileNavContainer}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10000,
            }}
          >
            <Links />
          </Paper>
        )}
      </>
    </AppBar>
  )
}

export default ApplicationBar
